<template>
  <div class="page-wrapper">
    <topPanel @reset-view="handleResetView" />
    <div class="grid">
      <div class="col left">
        <transition name="slide-left" appear>
          <appConfigLeft
            :config-data="sortedConfigs"
            :fi-number="fiStore.getFiNumber"
            :selected-config="selectedConfig"
            @selected-config="handleConfigSelect"
            @delete-config="handleDeleteConfig"
          >
          </appConfigLeft>
        </transition>
      </div>
      <div class="col middle">
        <transition name="slide-up" appear>
          <buildVersionsMiddle
            :build-data="sortedBuilds"
            :platform="selectedPlatform"
            :selected-config="selectedConfig"
            :selected-build-id="selectedBuildId"
            @platform-selected="handlePlatformSelect"
            @selected-build-id="handleBuildSelect"
          ></buildVersionsMiddle>
        </transition>
      </div>
      <div class="col right">
        <transition name="slide-right" appear>
          <buildDetailsRight
            v-if="selectedBuild"
            :build-id="selectedBuildId"
            :build="selectedBuild"
            @download-app="handleDownloadApp"
            @download-app-bundle="handleDownloadAppBundle"
            @download-archive="handleDownloadArchive"
            @delete-build="handleDeleteBuild"
            @undelete-build="handleUndeleteBuild"
          ></buildDetailsRight>
        </transition>

        <transition name="slide-right" appear>
          <div v-if="!selectedBuild" class="empty-panel">
            <!-- <p class="no-builds-message">
              📂 No Build Selected</p> -->
            <p class="no-builds-message">
              <span class="emoji"><q2-icon type="inbox"></q2-icon></span>
              <span class="text">No build selected</span>
            </p>
          </div>
        </transition>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted } from "vue";

import appConfigLeft from "@/components/appConfigLeft.vue";
import buildVersionsMiddle from "@/components/buildVersionsMiddle.vue";
import buildDetailsRight from "@/components/buildDetailsRight.vue";
import topPanel from "@/components/topPanel.vue";
import { useFiStore } from "../stores/buildStores";
import { useRouter, useRoute } from "vue-router";
import { logger } from "../utils/logger";
// import type { FiBuild } from "../types/build";

const fiStore = useFiStore();
const selectedConfig = ref(null);
const selectedBuildId = ref(null);
const selectedBuild = ref(null);
const selectedPlatform = ref("all");

// Builds marked for deletion stay listed for everyone. A soft-deleted build is
// still downloadable and still restorable until the TTL index purges it, so
// hiding it misrepresented the 10-day window as an immediate removal; the build
// list tints those rows red instead.
const configs = computed(() => fiStore.getFiData?.fiConfigs ?? []);
const router = useRouter();

const currentRoute = useRoute();

const activePanel = ref("left"); // default to left on load

const sortedConfigs = computed(() => {
  if (!configs.value.length) return [];

   return configs.value
     .map((c) => {
       const lstBld = [...c.builds]  // clone builds
         .sort((a, b) => (b.createdAt > a.createdAt ? 1 : -1))[0];

       return {
         configId: c.configId,
         configName: c.configName,
         nickName: c.nickname,
         fiNumber: c.fiNumber,
         legacyId: c.legacyId,
         lastBuild: lstBld?.createdAt,
         lastBuildFormatted: lstBld ? formatDate(lstBld.createdAt) : "",
       };
     })
     .sort((a, b) => (b.lastBuild > a.lastBuild ? 1 : -1));
});

const sortedBuilds = computed(() => {
  if (!configs.value.length || !selectedConfig.value) return [];
   const cfg = configs.value.find(c => c.configId === selectedConfig.value);
   return cfg ? [...cfg.builds].sort((a, b) =>
     (b.createdAt > a.createdAt ? 1 : -1)
   ) : [];
});

function getLatestBuildForConfig(configId, platform = "all") {
  if (!configs.value.length || !configId) return null;

  const cfg = configs.value.find((c) => c.configId === configId);
  if (!cfg?.builds?.length) return null;

  const normalizedPlatform = String(platform).toLowerCase();

  return [...cfg.builds]
    .filter((b) => {
      if (normalizedPlatform === "all") return true;
      return String(b.platform || "").toLowerCase() === normalizedPlatform;
    })
    .sort((a, b) => (b.createdAt > a.createdAt ? 1 : -1))[0] || null;
}

onMounted(async () => {
  const latestConfig = sortedConfigs.value?.[0] ?? null;

  if (latestConfig && !fiStore.getConfigId()) {
    // this has to be set before we get sorted builds
    selectedConfig.value = latestConfig.configId;

    const latestBuild = sortedBuilds.value[0] ?? null;

    if (latestBuild) {
      selectedBuildId.value = latestBuild.buildId;
      selectedBuild.value = latestBuild;
    }
    logger.debug("🔄 Selected Config:", selectedConfig.value);
    logger.debug("✅ Auto-selected latest build ID:", selectedBuildId.value);
  } else {
    selectedConfig.value = fiStore.getConfigId();

    if (fiStore.getBuildId()) {
      selectedBuildId.value = fiStore.getBuildId();
      selectedBuild.value = sortedBuilds.value?.find(
        (build) => build.buildId === selectedBuildId.value
      );
    }
  }

  router.replace({
    name: "FIAppsDynamic",
    params: {
      fiNumber: fiStore.getFiNumber,
      configId: selectedConfig.value,
      buildId: selectedBuildId.value,
    },
  });
});


function formatDate(iso) {
  const d = new Date(iso)
  const day = d.getDate()
  const suffix =
    day % 10 === 1 && day !== 11 ? 'st' :
    day % 10 === 2 && day !== 12 ? 'nd' :
    day % 10 === 3 && day !== 13 ? 'rd' : 'th'

  return `${d.toLocaleString('en-US', { month: 'long' })} ${day}${suffix} ${d.getFullYear()}`
}


function handleResetView() {
  logger.debug("reset view");
  selectedConfig.value = "";
  selectedBuildId.value = "";
  handleBuildSelect("");
  activePanel.value = "left";

  router.push({
    name: "FIAppsDynamic",
    params: {
      fiNumber: fiStore.getFiNumber,
    },
  });
}

function handleConfigSelect(config) {
  logger.debug("config change");
  logger.debug("✅ Config selected:", config);
  selectedConfig.value = config;

  const latestBuild = getLatestBuildForConfig(config, selectedPlatform.value);
  selectedBuildId.value = latestBuild?.buildId || "";
  selectedBuild.value = latestBuild || null;

  // activePanel.value = "middle";
  if (
    currentRoute.name !== "FIAppsDynamic" ||
    currentRoute.params.configId !== selectedConfig.value ||
    currentRoute.params.buildId !== selectedBuildId.value
  ) {
    router.push({
      name: "FIAppsDynamic",
      params: {
        fiNumber: fiStore.getFiNumber,
        configId: selectedConfig.value,
        buildId: selectedBuildId.value || undefined,
      },
    });
  }
}

function handlePlatformSelect(platform) {
  selectedPlatform.value = platform;
  logger.debug("✅ platform selected:", platform);
}

function handleBuildSelect(buildId) {
  selectedBuildId.value = buildId;

  selectedBuild.value = sortedBuilds.value?.find(
    (build) => build.buildId === buildId
  );

  logger.debug("✅ Build selected:", buildId);

  router.push({
    name: "FIAppsDynamic",
    params: {
      fiNumber: fiStore.getFiNumber,
      configId: selectedConfig.value,
      buildId,
    },
  });
}

async function handleDownloadApp() {
  try {
    await fiStore.downloadApp(selectedBuildId.value);
  } catch (err) {
    console.error("❌ Failed to download app:", err);
    alert("File could not be downloaded. Please request another build.");
  }
}
async function handleDownloadAppBundle() {
  try {
    await fiStore.downloadAppBundle(selectedBuildId.value);
  } catch (err) {
    console.error("❌ Failed to download app bundle:", err);
    alert("File could not be downloaded. Please request another build.");
  }
}
async function handleDownloadArchive() {
  try {
    await fiStore.downloadBuildArchive(selectedBuildId.value);
  } catch (err) {
    console.error("❌ Failed to download archive:", err);
    alert("File could not be downloaded. Please request another build.");
  }
}
// selectedBuild holds a snapshot object, and loadFiDataFromAPI replaces the
// whole payload — so after a refresh it points at a detached, pre-delete copy.
// Re-resolve it from the refreshed list so the detail panel picks up the new
// markedForDeletion state. A soft-deleted build is still listed, so it normally
// stays selected; the fallback covers a build that is gone from the payload
// entirely, such as one the TTL index has already purged.
function resyncSelectedBuild(buildId) {
  const stillListed = sortedBuilds.value?.find((b) => b.buildId === buildId);

  if (stillListed) {
    selectedBuild.value = stillListed;
  } else {
    handleConfigSelect(selectedConfig.value);
  }
}

async function handleDeleteBuild() {
  const confirmDelete = window.confirm(
    "Are you sure you want to delete this build?"
  );
  if (!confirmDelete) return;

  try {
    const objectId = selectedBuildId.value;
    if (!objectId) {
      console.error("❌ Cannot delete: build objectId not found");
      alert("Could not determine build ID to delete");
      return;
    }

    logger.debug("🔥 Attempting delete for build objectId:", objectId);
    await fiStore.deleteBuild(objectId); // 🔁 Pass actual _id to store
    alert("Build deleted successfully");

    await fiStore.loadFiDataFromAPI(); // refresh
    resyncSelectedBuild(objectId);
  } catch (err) {
    console.error("❌ Failed to delete build:", err);
    alert("Failed to delete build");
  }
}

async function handleDeleteConfig(configId) {
  logger.debug("🔥 Attempting delete for:", configId);
  try {
    await fiStore.deleteAllBuildsByConfig(configId);
    await fiStore.loadFiDataFromAPI();
    resyncSelectedBuild(selectedBuildId.value);
  } catch (err) {
    console.error("❌ Error deleting config builds:", err);
  }
}

async function handleUndeleteBuild() {
  try {
    const objectId = selectedBuildId.value;
    if (!objectId) {
      console.error("❌ Cannot undelete: build objectId not found");
      return;
    }

    logger.debug("🧼 Unmarking build:", objectId);
    await fiStore.undeleteBuild(objectId);
    alert("Build undeleted successfully");

    await fiStore.loadFiDataFromAPI(); // refresh data
    resyncSelectedBuild(objectId);
  } catch (err) {
    console.error("❌ Failed to undelete build:", err);
  }
}
</script>

<style scoped>
.page-wrapper {
  height: 100vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  height: calc(100vh - 64px);
  gap: 0px;
  overflow: hidden;
}

.col {
  min-height: 0;
  max-height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 15px;
  padding-bottom: 15px !important; /* Ensure consistent bottom padding */
  position: relative;
  /* Prevent overscroll on desktop */
  overscroll-behavior-y: contain;
  scroll-padding-bottom: 0;
  box-sizing: border-box;
}

/* Ensure child components don't add extra bottom margin */
.col > *:last-child {
  margin-bottom: 0;
}

/* Hide scrollbars by default, show only when scrolling */
.col::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.col::-webkit-scrollbar-track {
  background: transparent;
}

.col::-webkit-scrollbar-thumb {
  background-color: rgba(0,0,0,0);
  border-radius: 4px;
  transition: background-color 0.15s ease-in-out;
}

/* Show scrollbar only when hovering the scrollable area */
.col:hover::-webkit-scrollbar-thumb {
  background-color: rgba(0,0,0,0.15);
  transition-delay: 0.5s; /* Delay showing on hover */
}

/* Immediately show when actively scrolling/dragging */
.col::-webkit-scrollbar-thumb:active {
  background-color: rgba(0,0,0,0.3);
  transition-delay: 0s;
}

/* Firefox - minimal scrollbar */
.col {
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;
  transition: scrollbar-color 0.15s ease-in-out;
}

.col:hover {
  scrollbar-color: rgba(0,0,0,0.15) transparent;
  transition-delay: 0.5s;
}

/* Hide scrollbars completely on mobile since touch scrolling doesn't need them */
@media (max-width: 768px) {
  .col::-webkit-scrollbar {
    display: none;
  }

  .col {
    scrollbar-width: none;
    -ms-overflow-style: none;
  }
}

/* .right, .middle {
    padding: 0px 5px 0px 5px;
} */

.empty-panel {
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 200px;
}


@media (max-width: 1200px) {
  .grid {
    grid-template-columns: 1fr 2fr;
    grid-template-rows: 1fr 1fr;
    height: calc(100vh - 64px);
  }

  .left {
    grid-column: 1;
    grid-row: 1;
    overflow-y: auto;
    overscroll-behavior-y: contain;
  }

  .middle {
    grid-column: 1;
    grid-row: 2;
    overflow-y: auto;
    overscroll-behavior-y: contain;
  }

  .right {
    grid-column: 2;
    grid-row: 1 / 3; /* Span both rows */
    overflow-y: auto;
    overscroll-behavior-y: contain;
  }

}



@media (max-width: 768px) {
  .page-wrapper {
    height: auto;
    min-height: 100vh;
    overflow: visible;
  }

  .grid {
    grid-template-columns: 1fr;
    grid-template-rows: auto auto auto;
    gap: 0;
    height: auto;
    min-height: calc(100vh - 64px);
    overflow: visible;
  }

  .col {
    border-bottom: 1px solid #e5e7eb; /* light neutral */
    padding-bottom: 16px !important;
    margin-bottom: 16px;
    /* Remove max-height constraint for better content visibility */
    max-height: none;
    min-height: auto;
    overflow-y: visible;
    overscroll-behavior-y: auto;
    -webkit-overflow-scrolling: touch; /* Smooth scrolling on iOS */
  }

  .col:last-child {
    border-bottom: none;
    margin-bottom: 0;
  }

  /* Only constrain height for middle panel (build list) if needed */
  .col.middle {
    max-height: 50vh;
    overflow-y: auto;
  }

  /* Keep same order as desktop: Config (left) → Builds (middle) → Details (right) */
  .left {
      grid-column: 1;
      grid-row: 1;
      align-self: start;
  }
  .middle {
      grid-column: 1;
      grid-row: 2;
  }
  .right {
      grid-column: 1;
      grid-row: 3;
  }

  /* Add section labels for mobile clarity */
  .col::before {
    content: '';
    display: block;
    font-weight: 600;
    font-size: 14px;
    color: #666;
    margin-bottom: 12px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .col.left::before {
    content: 'Build Configurations';
  }

  .col.middle::before {
    content: 'Available Builds';
  }

  .col.right::before {
    content: 'Build Details & Download';
  }

  .settings-section {
    display: none;
  }

  .settings-section:nth-of-type(1) {
    display: block;
  }
}
</style>
